import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { AUDIENCES, AUDIENCE_LIST } from "../components/home/audiences";

const reveal = (i = 0) => ({ initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] } });

function Hero({ a }) {
  return (
    <section className="relative bg-[#0B0B0B] text-[#FAF7F2] overflow-hidden">
      <motion.img src={a.image} alt="" initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "linear" }} className="absolute inset-0 w-full h-full object-cover opacity-55" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
      <div className="relative container-wide px-5 md:px-12 py-24 md:py-36">
        <motion.p {...reveal(0)} className="text-[11px] uppercase tracking-[0.28em] text-[var(--gold)]">{a.eyebrow}</motion.p>
        <motion.h1 {...reveal(1)} className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl tracking-tight mt-4 max-w-3xl leading-[1.04]">{a.title}</motion.h1>
        <motion.p {...reveal(2)} className="font-script text-[var(--gold)] text-5xl sm:text-6xl lg:text-7xl leading-none">{a.accent}</motion.p>
        <motion.p {...reveal(3)} className="text-white/75 text-base md:text-lg max-w-xl mt-6">{a.blurb}</motion.p>
        <motion.div {...reveal(4)} className="flex flex-wrap gap-3 mt-8">
          <Link to={a.cta.to} data-testid="landing-hero-cta" className="btn-gold">{a.cta.label} <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/site/emmanuel-and-jennifer" data-testid="landing-demo-link" className="btn-outline-gold !text-white !border-white/40 hover:!bg-white hover:!text-black">See a live demo</Link>
        </motion.div>
      </div>
    </section>
  );
}

function Features({ a }) {
  return (
    <section className="section-pad">
      <div className="container-wide px-5 md:px-12">
        <motion.h2 {...reveal()} className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Everything you need, <span className="font-script text-[var(--gold)]">beautifully</span> in one place.</motion.h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-4 mt-10" data-testid="landing-feature-grid">
          {a.features.map((f, i) => (
            <motion.div key={f.t} {...reveal(i)} className={`lift rounded-3xl border border-[var(--line)] bg-white p-6 md:p-7 ${i === 0 || i === 5 ? "lg:col-span-6" : "lg:col-span-3"}`}>
              <span className="w-11 h-11 rounded-2xl bg-[var(--bg-warm)] flex items-center justify-center"><f.icon className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.6} /></span>
              <p className="font-serif text-xl md:text-2xl mt-4">{f.t}</p>
              <p className="text-sm text-[var(--muted)] mt-1">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Steps({ a }) {
  return (
    <section className="bg-[var(--bg-warm)] section-pad">
      <div className="container-wide px-5 md:px-12">
        <motion.p {...reveal()} className="font-script text-[var(--gold)] text-4xl">How it works</motion.p>
        <div className="relative grid md:grid-cols-4 gap-6 mt-8">
          <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: "easeInOut" }} className="hidden md:block absolute top-6 left-6 right-6 h-px bg-[#C8A96B]/50 origin-left" />
          {a.steps.map((s, i) => (
            <motion.div key={s} {...reveal(i + 1)} className="relative">
              <span className="relative z-10 w-12 h-12 rounded-full bg-[var(--ink)] text-[var(--gold)] font-serif text-xl flex items-center justify-center">{i + 1}</span>
              <p className="font-serif text-xl mt-4">{s}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ a }) {
  return (
    <section className="section-pad">
      <div className="container-wide px-5 md:px-12 grid lg:grid-cols-2 gap-8 items-center">
        <motion.div {...reveal()}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--gold-deep,#A38243)]">Simple pricing</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl mt-2">Start today. Upgrade when you're ready.</h2>
          <Link to="/pricing" className="inline-flex items-center gap-1 text-sm text-[var(--gold)] mt-4" data-testid="landing-pricing-link">Compare all plans <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
        <motion.div {...reveal(1)} data-testid="landing-pricing-card" className="rounded-[2rem] bg-[var(--ink)] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#C8A96B]/20 blur-3xl" />
          <p className="font-serif text-3xl md:text-4xl relative">{a.price.label}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2 relative">{a.price.detail}</p>
          <ul className="mt-6 space-y-2 relative">
            {a.features.slice(0, 4).map((f) => <li key={f.t} className="flex items-center gap-2 text-sm text-white/85"><Check className="w-4 h-4 text-[var(--gold)]" /> {f.t}</li>)}
          </ul>
          <Link to={a.cta.to} data-testid="landing-pricing-cta" className="btn-gold mt-8 relative">{a.cta.label}</Link>
        </motion.div>
      </div>
    </section>
  );
}

function OtherAudiences({ a }) {
  return (
    <section className="bg-[var(--ink)] text-white py-16" data-testid="landing-cta-strip">
      <div className="container-wide px-5 md:px-12 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <p className="font-serif text-3xl md:text-4xl">Forever starts <span className="font-script text-[var(--gold)]">here.</span></p>
        <div className="flex flex-wrap gap-2">
          {AUDIENCE_LIST.filter((x) => x.key !== a.key).map((x) => (
            <Link key={x.key} to={x.path} data-testid={`landing-switch-${x.key}`} className="btn-outline-gold !text-white !border-white/30 hover:!bg-white hover:!text-black">For {x.tab.toLowerCase()}</Link>
          ))}
          <Link to={a.cta.to} className="btn-gold" data-testid="landing-final-cta">{a.cta.label}</Link>
        </div>
      </div>
    </section>
  );
}

export default function AudienceLanding({ audience }) {
  const a = AUDIENCES[audience];
  return (
    <div className="bg-[var(--bg)] overflow-x-clip" data-testid={`landing-page-${a.key}`}>
      <Seo title={`LockedInForever ${a.eyebrow} — ${a.title} ${a.accent}`} description={a.blurb} path={a.path} />
      <Navbar overlay />
      <Hero a={a} />
      <Features a={a} />
      <Steps a={a} />
      <Pricing a={a} />
      <OtherAudiences a={a} />
      <Footer />
    </div>
  );
}
